import React from 'react';
import './analytics.css';

/**
 * LeaderboardTable - Ranked venue list
 *
 * Props:
 *   data       - Array of { rank, venueName, totalEngagement, ... }
 *   columns    - Array of { key, label, format? } (optional, default: rank + name + engagement)
 *   title      - Table title (optional)
 *   loading    - Show loading skeleton (optional)
 *   emptyText  - Text shown when no data (optional)
 *   onRowClick - Callback when row clicked (optional)
 */
export default function LeaderboardTable({
  data = [],
  columns,
  title,
  loading,
  emptyText = 'No engagement data yet',
  onRowClick,
}) {
  const defaultColumns = [
    { key: 'rank', label: '#', format: (v) => v },
    { key: 'venueName', label: 'Venue' },
    { key: 'totalEngagement', label: 'Engagements', format: (v) => v?.toLocaleString() },
  ];

  const cols = columns || defaultColumns;

  if (loading) {
    return (
      <div className="an-leaderboard">
        {title && <h4 className="an-chart-title">{title}</h4>}
        <div className="an-table-skeleton">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="an-table-skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="an-leaderboard">
        {title && <h4 className="an-chart-title">{title}</h4>}
        <div className="an-chart-empty" style={{ height: 200 }}>
          <span>{emptyText}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="an-leaderboard">
      {title && <h4 className="an-chart-title">{title}</h4>}
      <table className="an-leaderboard-table">
        <thead>
          <tr>
            {cols.map((col) => (
              <th key={col.key} className={`an-lb-th an-lb-th--${col.key}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={i}
              className={`an-lb-row ${onRowClick ? 'an-lb-row--clickable' : ''} ${i < 3 ? 'an-lb-row--top3' : ''}`}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {cols.map((col) => (
                <td key={col.key} className={`an-lb-td an-lb-td--${col.key}`}>
                  {col.key === 'rank' && i < 3 ? (
                    <span className={`an-lb-medal an-lb-medal--${i + 1}`}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                    </span>
                  ) : null}
                  {col.format ? col.format(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
